// PSD channel image data decoder.
//
// Operates on the structural output of `parsePsd`: walks each layer's
// channel block (using `channelImageOffset` and the per-channel `length`s),
// dispatches to the right decompressor, then assembles the resulting
// channel planes into a tightly-packed RGBA8 buffer sized to the layer's
// bounding rect.
//
// What this file does NOT do (deferred):
//   - 16/32-bit pixel fidelity beyond a downsample to 8-bit per channel
//     (full bit-depth path is M9).  The downsample is documented inline.
//
// Color mode support:
//   - RGB (3): unchanged — byte-for-byte identical to previous behaviour.
//   - CMYK (4): inverted channels converted to RGB at decode time.
//   - Lab (9): L*a*b* converted to RGB via D50 white point at decode time.
//   - Grayscale (1): single gray channel replicated to R=G=B.
//   - Indexed (2): palette lookup via colorModeData (256×3 bytes).
//
// Layer mask channel data (channel ID -2) IS now handled via decodeLayerMask().
// Channel -3 (real user mask) is still skipped.

import { BinaryReader } from "./BinaryReader";
import {
  decodePackBits,
  decodeRaw,
  decodeZip,
  decodeZipPrediction,
} from "./decompressors";
import {
  cmykToRgba,
  grayscaleToRgba,
  indexedToRgba,
  labToRgba,
} from "../../color/colorModeConvert";
import type {
  PsdChannelInfo,
  PsdDepth,
  PsdFile,
  PsdLayerRecord,
} from "./types";
import { PsdColorMode } from "./types";

export interface DecodedLayer {
  /** Tightly packed RGBA, row-major, top-down. */
  rgba: Uint8ClampedArray;
  width: number;
  height: number;
  /** True iff the source layer carried a transparency channel (id -1 or 3). */
  hasAlpha: boolean;
}

/**
 * Decoded user layer mask (channel id -2).
 *
 * The mask is a single-channel 8-bit grayscale image at the mask's own
 * bounding rectangle (which may differ from the layer's color bounds).
 */
export interface DecodedLayerMask {
  /** Grayscale bytes, one byte per pixel, row-major top-down. */
  gray: Uint8Array;
  width: number;
  height: number;
  /** Document-space top of the mask rectangle. */
  top: number;
  /** Document-space left of the mask rectangle. */
  left: number;
  /** Default color for pixels outside the mask rect (0 = hidden, 255 = visible). */
  defaultColor: number;
}

/** PSD compression codes, per spec. */
const COMP_RAW = 0;
const COMP_RLE = 1;
const COMP_ZIP = 2;
const COMP_ZIP_PRED = 3;

/**
 * Decode the channel image data for a single layer record.
 *
 * Returns `null` if the layer has zero area (e.g. effectively-empty group
 * dividers can have top==bottom or left==right).  Otherwise produces a
 * fully-assembled RGBA8 buffer of `width * height * 4` bytes.
 *
 * Layer mask channels (`id = -2` real-mask, `-3` user-mask) are recognised
 * and skipped: they belong to a separately-located mask rectangle and are
 * not part of the layer's color data.  Their bytes are still consumed so
 * the cursor stays aligned for later channels.
 */
export async function decodeLayer(
  file: PsdFile,
  layer: PsdLayerRecord,
  sourceBytes: Uint8Array,
): Promise<DecodedLayer | null> {
  const width = layer.right - layer.left;
  const height = layer.bottom - layer.top;
  if (width <= 0 || height <= 0) return null;

  const planes = await decodeChannelPlanes(file, layer.channels, sourceBytes, {
    startOffset: layer.channelImageOffset,
    width,
    height,
    sharedRleScanlineTable: false,
  });

  return assembleRgba(
    planes,
    width,
    height,
    file.header.depth,
    file.header.colorMode,
    file.colorModeData,
  );
}

/**
 * Decode the user layer mask for a layer (channel id -2).
 *
 * The mask pixel data is stored as channel id -2 in the layer's channel
 * image data section — NOT in the `maskData` metadata block (which only
 * contains the mask bounding rectangle and flags).
 *
 * Returns `null` if the layer has no channel -2, or if the maskData block
 * is too short to contain a valid bounding rectangle.
 */
export async function decodeLayerMask(
  file: PsdFile,
  layer: PsdLayerRecord,
  sourceBytes: Uint8Array,
): Promise<DecodedLayerMask | null> {
  // Find channel -2 (user layer mask).
  const maskCh = layer.channels.find((c) => c.id === -2);
  if (!maskCh) return null;

  // Parse mask bounds from the maskData metadata block.
  const md = layer.maskData;
  if (!md || md.length < 18) return null;

  const readI32 = (pos: number): number => {
    const u = (md[pos]! << 24) | (md[pos + 1]! << 16) | (md[pos + 2]! << 8) | md[pos + 3]!;
    return u > 0x7fff_ffff ? u - 0x1_0000_0000 : u;
  };
  const top    = readI32(0);
  const left   = readI32(4);
  const bottom = readI32(8);
  const right  = readI32(12);
  const defaultColor = md[16] ?? 0;

  const width  = right - left;
  const height = bottom - top;
  if (width <= 0 || height <= 0) return null;

  // Walk the channel image data to find the byte offset of channel -2.
  // Channels are stored in the same order as layer.channels, sequentially.
  const r = new BinaryReader(sourceBytes);
  r.seek(layer.channelImageOffset);
  const psb = file.header.version === 2;

  for (const ch of layer.channels) {
    const channelEnd = r.tell() + ch.length;
    if (ch.id === -2) {
      // Decode this channel using the mask dimensions (NOT the layer bounds).
      const compression = r.readU16();
      const plane = await decodeOneChannelPayload({
        compression,
        r,
        width,
        height,
        depth: file.header.depth,
        rleScanlineLengths:
          compression === COMP_RLE
            ? readScanlineLengths(r, psb, height)
            : null,
        payloadByteLimit: channelEnd - r.tell(),
      });
      // For depths > 8-bit, downsample to 8-bit the same way assembleRgba does.
      const gray = normalizeMaskTo8bit(plane, width * height, file.header.depth);
      return { gray, width, height, top, left, defaultColor };
    }
    r.seek(channelEnd);
  }
  return null;
}

/** Convert a mask plane to a packed 8-bit grayscale array. */
function normalizeMaskTo8bit(
  plane: Uint8Array,
  pixelCount: number,
  depth: PsdDepth,
): Uint8Array {
  if (depth === 8) {
    // Already 8-bit; return a copy to avoid aliasing.
    const out = new Uint8Array(pixelCount);
    for (let i = 0; i < pixelCount; i++) out[i] = plane[i] ?? 0;
    return out;
  }
  const out = new Uint8Array(pixelCount);
  if (depth === 16) {
    for (let i = 0; i < pixelCount; i++) out[i] = plane[i * 2] ?? 0;
  } else if (depth === 32) {
    const view = new DataView(plane.buffer, plane.byteOffset, plane.byteLength);
    for (let i = 0; i < pixelCount; i++) {
      const v = view.getFloat32(i * 4, false);
      out[i] = v <= 0 ? 0 : v >= 1 ? 255 : Math.round(v * 255);
    }
  } else {
    // depth === 1: 1-bit bitmap
    for (let i = 0; i < pixelCount; i++) {
      const byte = plane[i >>> 3] ?? 0;
      const bit  = (byte >>> (7 - (i & 7))) & 1;
      out[i] = bit ? 255 : 0;
    }
  }
  return out;
}

/**
 * Decode the document-level merged composite (section 5).
 *
 * The merged composite differs from per-layer channels in two ways:
 *   1. The first 2 bytes (compression code) appear ONCE for the whole
 *      block, not once per channel.
 *   2. For RLE the scanline-count table that follows covers `channels *
 *      height` rows in one contiguous run, not per-channel.
 *
 * Channel order is dictated by the document color mode.  For RGB docs the
 * planes are [R, G, B] (and optionally [R, G, B, A]).  We honour that order
 * directly; non-RGB modes will need a remap helper later.
 */
export async function decodeMergedComposite(
  file: PsdFile,
  sourceBytes: Uint8Array,
): Promise<DecodedLayer> {
  const { width, height, channels: channelCount, depth } = file.header;
  const r = new BinaryReader(sourceBytes);
  r.seek(file.imageDataOffset);
  const compression = r.readU16();
  const bytesPerSample = depth / 8;
  const rowBytesPerChannel = width * bytesPerSample;
  const planeBytes = rowBytesPerChannel * height;

  // For RLE, the scanline-count table covers all rows of all channels in
  // one contiguous stretch right after the compression code.
  let scanlineLengths: number[] | null = null;
  if (compression === COMP_RLE) {
    scanlineLengths = readScanlineLengths(
      r,
      file.header.version === 2,
      channelCount * height,
    );
  }

  const colorMode = file.header.colorMode;

  const planes: ChannelPlane[] = [];
  for (let c = 0; c < channelCount; c++) {
    // The merged composite has no per-channel ID list — we synthesise IDs
    // matching the document color mode so the assembler picks them up.
    const id = compositeChannelId(colorMode, c, channelCount);
    const plane = await decodeOneChannelPayload({
      compression,
      r,
      width,
      height,
      depth,
      // For RLE: slice exactly `height` entries from the shared table.
      rleScanlineLengths:
        scanlineLengths === null
          ? null
          : scanlineLengths.slice(c * height, (c + 1) * height),
      // For non-RLE: read directly from r up to the channel plane size.
      payloadByteLimit:
        compression === COMP_RAW
          ? planeBytes
          : sourceBytes.byteLength - r.tell(),
    });
    planes.push({ id, bytes: plane });
  }

  return assembleRgba(planes, width, height, depth, colorMode, file.colorModeData);
}

// ---------------------------------------------------------------------------
// Internals
// ---------------------------------------------------------------------------

interface ChannelPlane {
  id: number;
  /** Channel-plane bytes: `width * height * (depth/8)` long. */
  bytes: Uint8Array;
}

interface DecodeManyOpts {
  startOffset: number;
  width: number;
  height: number;
  /**
   * Per-layer mode (false): each channel has its own 2-byte compression
   * code + (for RLE) its own scanline table.
   * Merged-composite mode (true): one compression code, one shared table.
   * Only the per-layer path uses this helper today.
   */
  sharedRleScanlineTable: boolean;
}

async function decodeChannelPlanes(
  file: PsdFile,
  channels: PsdChannelInfo[],
  sourceBytes: Uint8Array,
  opts: DecodeManyOpts,
): Promise<ChannelPlane[]> {
  if (opts.sharedRleScanlineTable) {
    // Reserved for a future caller; merged composite walks its own path.
    throw new Error("sharedRleScanlineTable mode is handled inline elsewhere");
  }
  const r = new BinaryReader(sourceBytes);
  r.seek(opts.startOffset);
  const psb = file.header.version === 2;
  const planes: ChannelPlane[] = [];
  for (const ch of channels) {
    const channelEnd = r.tell() + ch.length;
    // Mask channels (-2 = user, -3 = real) are not part of the layer's
    // color image.  Their data lives on a different rectangle (see the
    // layer's maskData block).  Skip the bytes and move on.  TODO(M6): a
    // future decoder will read these via a sibling decodeLayerMask() call.
    if (ch.id === -2 || ch.id === -3) {
      r.seek(channelEnd);
      continue;
    }
    const compression = r.readU16();
    const plane = await decodeOneChannelPayload({
      compression,
      r,
      width: opts.width,
      height: opts.height,
      depth: file.header.depth,
      rleScanlineLengths:
        compression === COMP_RLE
          ? readScanlineLengths(r, psb, opts.height)
          : null,
      payloadByteLimit: channelEnd - r.tell(),
    });
    // Hard sync the cursor to channelEnd: some Photoshop versions emit a
    // trailing pad byte we don't otherwise account for.  Better to trust
    // the layer-declared length than the decompressor's bookkeeping.
    if (r.tell() !== channelEnd) r.seek(channelEnd);
    planes.push({ id: ch.id, bytes: plane });
  }
  return planes;
}

interface ChannelDecodeOpts {
  compression: number;
  r: BinaryReader;
  width: number;
  height: number;
  depth: PsdDepth;
  /** RLE per-row byte counts, or null for non-RLE compressions. */
  rleScanlineLengths: number[] | null;
  /** Maximum bytes (after RLE table) that this channel may consume. */
  payloadByteLimit: number;
}

async function decodeOneChannelPayload(
  opts: ChannelDecodeOpts,
): Promise<Uint8Array> {
  const { compression, r, width, height, depth } = opts;
  const bytesPerSample = depth / 8;
  const planeBytes = width * height * bytesPerSample;

  switch (compression) {
    case COMP_RAW: {
      const limit = Math.min(opts.payloadByteLimit, planeBytes);
      const raw = r.readBytes(limit);
      const out = new Uint8Array(planeBytes);
      out.set(raw);
      return out;
    }
    case COMP_RLE: {
      if (!opts.rleScanlineLengths) {
        throw new Error("internal: RLE compression with no scanline table");
      }
      const rowBytes = width * bytesPerSample;
      const out = new Uint8Array(planeBytes);
      let outOff = 0;
      for (let y = 0; y < height; y++) {
        const rowLen = opts.rleScanlineLengths[y]!;
        const encoded = r.readBytes(rowLen);
        const decoded = decodePackBits(encoded, rowBytes);
        out.set(decoded, outOff);
        outOff += rowBytes;
      }
      return out;
    }
    case COMP_ZIP: {
      const payload = r.readBytes(opts.payloadByteLimit);
      const decoded = await decodeZip(payload);
      return normalizePlaneSize(decoded, planeBytes);
    }
    case COMP_ZIP_PRED: {
      if (depth !== 8 && depth !== 16 && depth !== 32) {
        throw new Error(`ZIP-prediction with depth ${depth} unsupported (need 8/16/32)`);
      }
      const payload = r.readBytes(opts.payloadByteLimit);
      const decoded = await decodeZipPrediction(payload, width, height, depth);
      return normalizePlaneSize(decoded, planeBytes);
    }
    default:
      throw new Error(`unsupported PSD compression code ${compression}`);
  }
}

function normalizePlaneSize(
  decoded: Uint8Array,
  expected: number,
): Uint8Array {
  if (decoded.length === expected) return decoded;
  // Tolerate small under/over runs: zero-pad or truncate.  This protects
  // against a single trailing pad byte some files emit.
  const out = new Uint8Array(expected);
  out.set(decoded.subarray(0, Math.min(decoded.length, expected)));
  return out;
}

function readScanlineLengths(
  r: BinaryReader,
  psb: boolean,
  rowCount: number,
): number[] {
  const lengths: number[] = new Array<number>(rowCount);
  for (let i = 0; i < rowCount; i++) {
    lengths[i] = psb ? r.readU32() : r.readU16();
  }
  return lengths;
}

// ---------------------------------------------------------------------------
// Composite channel ID synthesis for the merged image (section 5).
// ---------------------------------------------------------------------------

/**
 * Synthesise a logical channel ID for the n-th plane of the document-level
 * merged composite, given the document color mode.
 *
 * The merged composite has no explicit channel-ID list; the plane order is
 * always dictated by the color mode.
 *
 *   RGB  (3): 0=R, 1=G, 2=B, then -1=A for the 4th channel.
 *   CMYK (4): 0=C, 1=M, 2=Y, 3=K, then -1=A for any 5th channel.
 *   Lab  (9): 0=L, 1=a, 2=b, then -1=A for the 4th channel.
 *   Gray (1): 0=gray, then -1=A for the 2nd channel.
 *   Indexed (2): 0=index.
 *   Fallback: same as RGB (won't be converted anyway).
 */
function compositeChannelId(
  colorMode: number,
  channelIndex: number,
  totalChannels: number,
): number {
  // For CMYK: first 4 planes = C(0), M(1), Y(2), K(3); beyond that = alpha(-1).
  if (colorMode === PsdColorMode.CMYK) {
    return channelIndex < 4 ? channelIndex : -1;
  }
  // For Grayscale: first plane = gray(0); beyond that = alpha(-1).
  if (colorMode === PsdColorMode.Grayscale) {
    return channelIndex === 0 ? 0 : -1;
  }
  // For RGB, Lab, Indexed: 0,1,2 = color channels, then -1 = alpha.
  // For RGB: 3 (as legacy alt-alpha ID) is used by some writers; -1 by others.
  // We emit -1 here so assembleRgba treats the 4th channel consistently.
  if (channelIndex < 3) return channelIndex;
  return -1;
}

// ---------------------------------------------------------------------------
// Channel-plane → RGBA8 assembly.
// ---------------------------------------------------------------------------

/**
 * Build an RGBA8 buffer from the per-channel planes, applying color-mode
 * conversion as needed.
 *
 * Channel-ID mapping for RGB-mode documents:
 *   0 → R, 1 → G, 2 → B, 3 → A, -1 → A
 * The two distinct "alpha" IDs reflect Photoshop's history: `-1` is the
 * canonical transparency-mask channel introduced with layers, while `3`
 * appears for the merged composite of RGB+A files and (rarely) for
 * standalone layers exported from older tools.  We accept either.
 *
 * For CMYK, Lab, Grayscale, and Indexed modes the planes are collected and
 * passed to the appropriate converter in `colorModeConvert.ts`; the RGB path
 * is byte-for-byte unchanged.
 *
 * For 16-bit channels each sample is `value >> 8` (drop low byte).  For
 * 32-bit float channels each sample is `clamp(value, 0, 1) * 255`.  This
 * is a baseline — the full high-bit-depth pipeline is M9.  Saving these as
 * 8-bit is lossy and must NOT be used as a writer source path.
 */
function assembleRgba(
  planes: ChannelPlane[],
  width: number,
  height: number,
  depth: PsdDepth,
  colorMode: number,
  colorModeData: Uint8Array,
): DecodedLayer {
  const total = width * height;

  // -------------------------------------------------------------------------
  // CMYK (4): collect C/M/Y/K + optional alpha, then convert.
  // -------------------------------------------------------------------------
  if (colorMode === PsdColorMode.CMYK) {
    // Normalise all planes to 8-bit first, then convert.
    const planeMap = collectPlanesAs8bit(planes, total, depth);
    const c = planeMap.get(0) ?? new Uint8Array(total);
    const m = planeMap.get(1) ?? new Uint8Array(total);
    const y = planeMap.get(2) ?? new Uint8Array(total);
    const k = planeMap.get(3) ?? new Uint8Array(total);
    const a = planeMap.get(-1) ?? planeMap.get(4) ?? null;
    const hasAlpha = a !== null;
    const rgba = cmykToRgba(c, m, y, k, a, total);
    return { rgba, width, height, hasAlpha };
  }

  // -------------------------------------------------------------------------
  // Lab (9): collect L/a/b + optional alpha, then convert.
  // -------------------------------------------------------------------------
  if (colorMode === PsdColorMode.Lab) {
    const planeMap = collectPlanesAs8bit(planes, total, depth);
    const lp = planeMap.get(0) ?? new Uint8Array(total);
    const ap = planeMap.get(1) ?? new Uint8Array(total);
    const bp = planeMap.get(2) ?? new Uint8Array(total);
    const alpha = planeMap.get(-1) ?? null;
    const hasAlpha = alpha !== null;
    const rgba = labToRgba(lp, ap, bp, alpha, total);
    return { rgba, width, height, hasAlpha };
  }

  // -------------------------------------------------------------------------
  // Grayscale (1): single channel → R=G=B.
  // -------------------------------------------------------------------------
  if (colorMode === PsdColorMode.Grayscale) {
    const planeMap = collectPlanesAs8bit(planes, total, depth);
    const gray = planeMap.get(0) ?? new Uint8Array(total);
    const alpha = planeMap.get(-1) ?? null;
    const hasAlpha = alpha !== null;
    const rgba = grayscaleToRgba(gray, alpha, total);
    return { rgba, width, height, hasAlpha };
  }

  // -------------------------------------------------------------------------
  // Indexed (2): palette lookup via colorModeData.
  // -------------------------------------------------------------------------
  if (colorMode === PsdColorMode.Indexed) {
    const planeMap = collectPlanesAs8bit(planes, total, depth);
    const idx = planeMap.get(0) ?? new Uint8Array(total);
    const rgba = indexedToRgba(idx, colorModeData, total);
    return { rgba, width, height, hasAlpha: false };
  }

  // -------------------------------------------------------------------------
  // RGB (3) — and any unrecognised mode: unchanged path.
  // -------------------------------------------------------------------------
  const rgba = new Uint8ClampedArray(total * 4);
  // Initialise alpha to opaque; specific alpha channel overwrites below.
  for (let i = 3; i < rgba.length; i += 4) rgba[i] = 255;

  let hasAlpha = false;
  for (const plane of planes) {
    const target = channelIdToOffset(plane.id);
    if (target === null) continue;
    if (target === 3) hasAlpha = true;
    writeSamplesIntoRgba(rgba, target, plane.bytes, total, depth);
  }

  // If no alpha plane was seen, we left the channel at the 255 fill above.
  return { rgba, width, height, hasAlpha };
}

/**
 * Normalise each plane in `planes` to a flat 8-bit-per-sample Uint8Array and
 * return a Map keyed by channel ID.
 *
 * This is used by the non-RGB converters which need plain 8-bit input regardless
 * of the source bit depth.  The same per-depth downsampling logic that
 * `writeSamplesIntoRgba` uses is applied here.
 */
function collectPlanesAs8bit(
  planes: ChannelPlane[],
  pixelCount: number,
  depth: PsdDepth,
): Map<number, Uint8Array> {
  const out = new Map<number, Uint8Array>();
  for (const plane of planes) {
    if (plane.id === -2 || plane.id === -3) continue; // skip mask channels
    out.set(plane.id, normalizeTo8bit(plane.bytes, pixelCount, depth));
  }
  return out;
}

/** Downscale a raw channel plane to 8-bit-per-sample. */
function normalizeTo8bit(
  plane: Uint8Array,
  pixelCount: number,
  depth: PsdDepth,
): Uint8Array {
  if (depth === 8) {
    const out = new Uint8Array(pixelCount);
    for (let i = 0; i < pixelCount; i++) out[i] = plane[i] ?? 0;
    return out;
  }
  const out = new Uint8Array(pixelCount);
  if (depth === 16) {
    for (let i = 0; i < pixelCount; i++) out[i] = plane[i * 2] ?? 0;
  } else if (depth === 32) {
    const view = new DataView(plane.buffer, plane.byteOffset, plane.byteLength);
    for (let i = 0; i < pixelCount; i++) {
      const v = view.getFloat32(i * 4, false);
      out[i] = v <= 0 ? 0 : v >= 1 ? 255 : Math.round(v * 255);
    }
  } else {
    // depth === 1
    for (let i = 0; i < pixelCount; i++) {
      const byte = plane[i >>> 3] ?? 0;
      const bit = (byte >>> (7 - (i & 7))) & 1;
      out[i] = bit ? 255 : 0;
    }
  }
  return out;
}

function channelIdToOffset(id: number): 0 | 1 | 2 | 3 | null {
  switch (id) {
    case 0:
      return 0; // R
    case 1:
      return 1; // G
    case 2:
      return 2; // B
    case 3:
    case -1:
      return 3; // A
    default:
      return null; // masks, spot channels, etc. — ignored here
  }
}

function writeSamplesIntoRgba(
  rgba: Uint8ClampedArray,
  target: 0 | 1 | 2 | 3,
  plane: Uint8Array,
  pixelCount: number,
  depth: PsdDepth,
): void {
  if (depth === 8) {
    for (let i = 0; i < pixelCount; i++) {
      rgba[i * 4 + target] = plane[i] ?? 0;
    }
    return;
  }
  if (depth === 16) {
    // 16-bit big-endian samples → drop the low byte.
    for (let i = 0; i < pixelCount; i++) {
      rgba[i * 4 + target] = plane[i * 2] ?? 0;
    }
    return;
  }
  if (depth === 32) {
    // 32-bit big-endian float samples → clamp [0,1] × 255.
    const view = new DataView(
      plane.buffer,
      plane.byteOffset,
      plane.byteLength,
    );
    for (let i = 0; i < pixelCount; i++) {
      const v = view.getFloat32(i * 4, false);
      const clamped = v <= 0 ? 0 : v >= 1 ? 1 : v;
      rgba[i * 4 + target] = Math.round(clamped * 255);
    }
    return;
  }
  if (depth === 1) {
    // 1-bit bitmap mode: one bit per sample, MSB first within each byte.
    // Photoshop stores 0 = black, 1 = white.  Treat as a grayscale 0/255.
    for (let i = 0; i < pixelCount; i++) {
      const byte = plane[i >>> 3] ?? 0;
      const bit = (byte >>> (7 - (i & 7))) & 1;
      rgba[i * 4 + target] = bit ? 255 : 0;
    }
    return;
  }
  throw new Error(`unsupported depth ${depth as number}`);
}
